/* Owner rule 2026-10-11: no video ever shows a failure message, a play-button overlay or an "open the film" link.
   A video that fails, stalls or is refused autoplay quietly shows its poster (or keeps its last frame) and retries:
   with backoff while it stays on screen, and again on the next tap/scroll (iOS Low Power Mode refuses autoplay until a gesture).
   iOS root cause fixed here: release() empties src and calls load(); WebKit then fires an asynchronous "error" (empty src)
   which arrived after the next start() had set the src again, so the player marked itself failed and showed the message.
   Errors are now only accepted for the current source (token + currentSrc + MediaError code). */
(() => {
  if (window.LimitlessVideo) { window.LimitlessVideo.init(document); return; }
  const players = new Map();
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const threshold = 0.25;
  let current = null, queued = false;
  const inDocument = player => player.frame.isConnected && !player.frame.closest('[hidden]');
  const visible = player => {
    if (!inDocument(player) || document.hidden) return false;
    const box = player.frame.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
  };
  const automaticAllowed = () => Boolean(observer) && !motion?.matches && !connection?.saveData && !document.hidden;
  const paint = player => {
    const playing = !player.video.paused && !player.video.ended;
    player.button.hidden = true;
    player.video.hidden = !player.hasPlayed && !player.loading;
    player.poster.hidden = player.hasPlayed;
    player.frame.dataset.llVideoState = player.failed ? 'error' : player.blocked ? 'blocked' : player.loading ? 'loading' : playing ? 'playing' : player.hasPlayed ? 'paused' : 'idle';
  };
  /* never any visible failure UI: keep fallback/status/button hidden for good */
  const quietUI = player => {
    if (player.status) player.status.hidden = true;
    if (player.fallback) player.fallback.hidden = true;
    if (player.button) player.button.hidden = true;
  };
  const feedback = player => { quietUI(player); retryLater(player); };
  const BACKOFF = [1200, 3000, 6000, 12000, 20000, 30000];
  const retryLater = player => {
    if (player.disposed) return;
    clearTimeout(player.retryTimer);
    const wait = BACKOFF[Math.min(player.retries || 0, BACKOFF.length - 1)];
    player.retries = (player.retries || 0) + 1;
    player.retryTimer = setTimeout(() => {
      if (player.disposed) return;
      player.failed = false; player.blocked = false;
      if (player.video.hasAttribute('src')) { player.video.removeAttribute('src'); player.ignoreErrorsUntil = Date.now() + 1500; player.video.load(); }
      schedule();
    }, wait);
  };
  /* a gesture anywhere lets iOS start a refused (Low Power Mode) or failed video again */
  let gestureBound = false;
  const onGesture = () => { players.forEach(p => { if (p.blocked || p.failed) { p.blocked = false; p.failed = false; } }); schedule(); };
  const bindGesture = () => {
    if (gestureBound) return; gestureBound = true;
    ['touchend', 'click', 'keydown'].forEach(t => document.addEventListener(t, onGesture, {passive:true, capture:true}));
    window.addEventListener('scroll', () => { clearTimeout(bindGesture.t); bindGesture.t = setTimeout(onGesture, 250); }, {passive:true});
  };
  const stop = player => {
    player.token += 1;
    player.loading = false;
    if (!player.video.paused) {
      player.internalPause = true;
      player.video.pause();
    }
    paint(player);
  };
  const release = player => {
    stop(player);
    if (player.video.hasAttribute('src')) {
      player.video.removeAttribute('src');
      player.ignoreErrorsUntil = Date.now() + 1500;
      player.video.load();
    }
    player.hasPlayed = false;
    player.manual = false;
    player.abortRetries = 0;
    paint(player);
    if (current === player) current = null;
  };
  const failure = player => {
    if (player.disposed || !player.video.hasAttribute('src')) return;
    const err = player.video.error, want = player.video.dataset.llVideoSrc || '';
    /* stale or aborted errors (emptied src, superseded load) are not failures */
    if (!err || err.code === 1 || Date.now() < (player.ignoreErrorsUntil || 0)) return;
    if (want && player.video.currentSrc && player.video.currentSrc.split('?')[0] !== new URL(want, location.href).href.split('?')[0]) return;
    stop(player);
    player.failed = true;
    player.hasPlayed = false;
    feedback(player);
    paint(player);
  };
  const start = async (player, manual = false) => {
    if (player.disposed || player.loading || !visible(player)) return;
    if (!manual && (!automaticAllowed() || player.userPaused || player.blocked || player.failed || player.ratio < threshold)) return;
    players.forEach(other => { if (other !== player && (!other.video.paused || other.loading)) stop(other); });
    current = player;
    player.manual = manual;
    if (manual) player.abortRetries = 0;
    player.loading = true;
    quietUI(player);
    player.video.muted = true;
    player.video.defaultMuted = true;
    player.video.playsInline = true;
    player.video.loop = true;
    const token = ++player.token;
    clearTimeout(player.watchdog);
    player.watchdog = setTimeout(() => { if (token === player.token && player.loading && !player.disposed) { stop(player); player.failed = true; retryLater(player); } }, 15000);
    // Keep the media surface visible while loading. Its native poster supplies
    // the still image without a CSS visibility dependency on the playing event.
    if (player.poster.classList.contains('ll-hero-poster-unavailable')) player.video.removeAttribute('poster');
    else player.video.poster = player.frame.closest('[data-ll-hero-film]') ? player.poster.src : player.poster.currentSrc || player.poster.src;
    player.video.preload = 'auto';
    paint(player);
    if (!player.video.getAttribute('src') || player.video.error) {
      player.video.src = player.video.dataset.llVideoSrc;
      player.video.load();
    }
    try {
      await player.video.play();
      if (player.disposed || token !== player.token) {
        if (player.disposed || current !== player || !player.video.hasAttribute('src') || !visible(player) || player.userPaused) stop(player);
        return;
      }
      if (!visible(player) || player.userPaused || (!manual && !automaticAllowed())) { stop(player); return; }
      player.loading = false;
      player.hasPlayed = true;
      player.retries = 0; clearTimeout(player.watchdog);
      paint(player);
    } catch (error) {
      if (player.disposed || token !== player.token) return;
      player.loading = false;
      stop(player);
      player.hasPlayed = false;
      clearTimeout(player.watchdog);
      if (error?.name === 'AbortError' && !player.video.error) {
        // A resource/visibility interruption is not an autoplay denial: retry quietly.
        if (player.abortRetries++ === 0) { paint(player); schedule(); return; }
        player.failed = true; retryLater(player);
      } else if (error?.name === 'NotAllowedError' && !player.video.error) {
        // iOS Low Power Mode / autoplay refused: poster stays, the next gesture starts it.
        player.blocked = true; bindGesture();
      } else {
        player.failed = true;
        feedback(player);
      }
      paint(player);
    }
  };
  const choose = () => {
    queued = false;
    if (document.hidden) { players.forEach(release); return; }
    players.forEach(player => {
      if (!visible(player) || player.ratio === 0) {
        if (player.video.hasAttribute('src') || player.loading || !player.video.paused) release(player);
      }
    });
    if (current && visible(current) && current.ratio > 0 && (current.userPaused || current.blocked)) return;
    if (current?.manual && visible(current) && current.ratio > 0 && (!current.video.paused || current.loading)) return;
    if (!automaticAllowed()) return;
    const candidates = [...players.values()].filter(player => visible(player) && player.ratio >= threshold && !player.userPaused && !player.blocked && !player.failed);
    if (!candidates.length) return;
    candidates.sort((a, b) => b.ratio - a.ratio || a.order - b.order);
    let target = candidates[0];
    if (current && candidates.includes(current) && current.ratio >= target.ratio - 0.1) target = current;
    if (current && current !== target) stop(current);
    if (target.video.paused && !target.loading) start(target);
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || (callback => setTimeout(callback, 0)))(choose);
  };
  const observer = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const player = players.get(entry.target);
      if (!player || player.disposed) return;
      player.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
      if (player.ratio === 0 || !inDocument(player)) release(player);
    });
    schedule();
  }, {threshold:[0,0.1,threshold,0.5,0.75,1]}) : null;
  const init = (root = document) => {
    const frames = [...(root.matches?.('[data-ll-video]') ? [root] : []), ...root.querySelectorAll('[data-ll-video]')];
    frames.forEach(frame => {
      if (players.has(frame)) return;
      const video = frame.querySelector('video'), button = frame.querySelector('[data-ll-video-play]'), poster = frame.querySelector('.ll-video-poster'), fallback = frame.querySelector('.ll-video-fallback'), status = frame.parentElement.querySelector('.ll-video-status');
      const playIcon = button?.querySelector('[data-ll-video-icon-play]'), pauseIcon = button?.querySelector('[data-ll-video-icon-pause]');
      if (!video || !poster || !video.dataset.llVideoSrc) return;
      const controller = new AbortController(), options = {signal:controller.signal};
      const player = {frame,video,button:button||document.createElement('button'),poster,fallback,status,controller,abortRetries:0,ratio:0,order:players.size,token:0,hasPlayed:false,loading:false,userPaused:false,blocked:false,failed:false,manual:false,internalPause:false,disposed:false};
      players.set(frame, player);
      video.muted = true; video.defaultMuted = true; video.playsInline = true; video.loop = true;
      quietUI(player); frame.classList.add('ll-video-frame--enhanced');
      button?.addEventListener('click', () => {
        if (!video.paused || player.loading) {
          player.userPaused = true;
          player.manual = false;
          current = player;
          stop(player);
        } else {
          player.userPaused = false; player.blocked = false; player.failed = false;
          if (player.ratio === 0) player.ratio = 1;
          start(player, true);
        }
      }, options);
      video.addEventListener('playing', () => {
        if (player.disposed) return;
        if (current !== player || !visible(player) || player.userPaused || (!player.manual && !automaticAllowed())) { stop(player); return; }
        player.loading = false; player.hasPlayed = true; paint(player);
      }, options);
      video.addEventListener('pause', () => {
        // Only the explicit pause control records a lasting user pause.
        // Native pauses may be caused by scrolling, visibility or media loading.
        player.internalPause = false; paint(player);
      }, options);
      video.addEventListener('ended', () => paint(player), options);
      video.addEventListener('error', () => failure(player), options);
      /* a stalled stream keeps its last frame (or the poster) and is restarted quietly */
      video.addEventListener('stalled', () => { if (player.disposed || !visible(player)) return; clearTimeout(player.stallTimer); player.stallTimer = setTimeout(() => { if (!player.disposed && visible(player) && player.video.readyState < 3 && player.video.hasAttribute('src')) { stop(player); player.failed = true; retryLater(player); } }, 8000); }, options);
      video.addEventListener('timeupdate', () => { clearTimeout(player.stallTimer); }, options);
      paint(player); observer?.observe(frame);
    });
    schedule();
  };
  const pause = (root = document) => {
    players.forEach(player => {
      if (root === document || root.contains(player.frame)) {
        if (document.hidden || !inDocument(player)) release(player); else stop(player);
      }
    });
  };
  const destroy = root => {
    players.forEach((player,frame) => {
      if (root === document || root === frame || root.contains(frame)) {
        player.disposed = true; player.controller.abort(); observer?.unobserve(frame); release(player);
        clearTimeout(player.retryTimer); clearTimeout(player.watchdog); clearTimeout(player.stallTimer);
        quietUI(player); player.frame.classList.remove('ll-video-frame--enhanced'); players.delete(frame);
      }
    });
    schedule();
  };
  const policyChanged = () => {
    if (motion?.matches || connection?.saveData) players.forEach(player => { if (!player.manual) release(player); });
    schedule();
  };
  document.addEventListener('visibilitychange', () => { if (document.hidden) players.forEach(release); else schedule(); });
  document.addEventListener('ll:panel:change', schedule);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  if (motion?.addEventListener) motion.addEventListener('change', policyChanged); else motion?.addListener?.(policyChanged);
  connection?.addEventListener?.('change', policyChanged);
  if (!observer) { window.addEventListener('scroll', schedule, {passive:true}); window.addEventListener('resize', schedule, {passive:true}); }
  window.LimitlessVideo = {init,pause,destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
})();

/* r3 2026-10-11 (owner rule: no video ever shows a failure state): every other site video (about-page reels, Skip This Line page,
   hero collage, inline page videos) heals quietly too. A failed or stalled video keeps its poster/last frame and is reloaded in
   the background with backoff; a tap/scroll (iOS Low Power Mode blocks autoplay) resumes videos that should be playing.
   Videos managed by the player above ([data-ll-video]) are skipped; reduced-motion visitors are left alone. */
(() => {
  if (window.__llVideoHeal) return; window.__llVideoHeal = true;
  const STEPS = [1500, 4000, 8000, 15000, 30000, 60000], tries = new WeakMap(), timers = new WeakMap();
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const owned = v => !!v.closest('[data-ll-video]') || v.hasAttribute('data-ll-no-heal');
  const onScreen = v => { const r = v.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth; };
  const wants = v => v.dataset.llWant === '1' || (v.autoplay && v.dataset.llWant !== '0');
  const play = v => { if (reduce?.matches) return; try { v.muted = true; const p = v.play(); p?.catch?.(() => {}); } catch {} };
  function heal(v) {
    if (!v?.isConnected || owned(v) || timers.get(v)) return;
    const n = tries.get(v) || 0; if (n >= STEPS.length) return;
    tries.set(v, n + 1);
    timers.set(v, setTimeout(() => {
      timers.delete(v);
      if (!v.isConnected || (!v.getAttribute('src') && !v.querySelector('source[src]'))) return;
      const t = v.currentTime || 0, src = v.getAttribute('src');
      if (src) v.setAttribute('src', src);
      try { v.load(); } catch {}
      if (t) v.addEventListener('loadedmetadata', () => { try { v.currentTime = t; } catch {} }, { once: true });
      if (wants(v) && onScreen(v)) play(v);
    }, STEPS[n]));
  }
  document.addEventListener('play', ev => { if (ev.target instanceof HTMLVideoElement) ev.target.dataset.llWant = '1'; }, true);
  document.addEventListener('pause', ev => { const v = ev.target; if (v instanceof HTMLVideoElement && !v.error && !v.ended) v.dataset.llWant = '0'; }, true);
  document.addEventListener('playing', ev => { if (ev.target instanceof HTMLVideoElement) tries.delete(ev.target); }, true);
  document.addEventListener('error', ev => {
    const t = ev.target;
    if (t instanceof HTMLVideoElement) return heal(t);
    if (t instanceof HTMLSourceElement && t.parentElement instanceof HTMLVideoElement) {
      let next = t.nextElementSibling; while (next && !(next instanceof HTMLSourceElement)) next = next.nextElementSibling;
      if (!next) heal(t.parentElement);
    }
  }, true);
  document.addEventListener('stalled', ev => {
    const v = ev.target; if (!(v instanceof HTMLVideoElement) || owned(v)) return;
    const t0 = v.currentTime;
    setTimeout(() => { if (v.isConnected && !v.paused && v.currentTime === t0 && v.readyState < 3) heal(v); }, 8000);
  }, true);
  const nudge = () => {
    if (reduce?.matches) return;
    document.querySelectorAll('video').forEach(v => {
      if (owned(v) || !wants(v) || !onScreen(v)) return;
      if (v.error) { tries.delete(v); clearTimeout(timers.get(v)); timers.delete(v); heal(v); }
      else if (v.paused) play(v);
    });
  };
  let nt = 0; const gesture = () => { const now = Date.now(); if (now - nt < 800) return; nt = now; nudge(); };
  for (const type of ['touchend', 'click', 'keydown']) document.addEventListener(type, gesture, { capture: true, passive: true });
})();
